import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Star, ThumbsUp, ThumbsDown, MessageSquare, Send, X } from 'lucide-react';
import { toast } from 'sonner';

export default function SessionFeedback({ sessionId, onClose, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [helpful, setHelpful] = useState(null);
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [concernsAddressed, setConcernsAddressed] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/sessions/${sessionId}/feedback`,
        {
          session_id: sessionId,
          rating,
          helpful,
          would_recommend: wouldRecommend,
          concerns_addressed: concernsAddressed,
          comment: comment.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Thank you for your feedback!');
      onSubmit && onSubmit();
      onClose && onClose();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Feedback already submitted');
      } else {
        toast.error('Failed to submit feedback');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const YesNoButton = ({ value, selected, onSelect, label }) => (
    <button
      onClick={() => onSelect(value)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
        selected === value
          ? value ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-400 bg-red-50 text-red-600'
          : 'border-gray-200 hover:border-gray-300 text-[#718096]'
      }`}
    >
      {value ? <ThumbsUp size={18} /> : <ThumbsDown size={18} />}
      <span className="text-sm font-medium">{label}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="feedback-modal">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-[#2D3748]">Session Feedback</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="close-feedback"
          >
            <X size={20} className="text-[#718096]" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Star Rating */}
          <div className="text-center">
            <p className="text-sm text-[#718096] mb-3">How would you rate this session?</p>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  data-testid={`star-${star}`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    size={36}
                    className={`transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm text-primary mt-2 font-medium">
                {rating === 5 ? 'Excellent!' : rating === 4 ? 'Very Good' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
              </p>
            )}
          </div>

          {/* Quick Feedback Questions */}
          <div className="space-y-4">
            <div>
              <p className="text-sm text-[#2D3748] mb-2">Was this session helpful?</p>
              <div className="flex gap-3">
                <YesNoButton value={true} selected={helpful} onSelect={setHelpful} label="Yes" />
                <YesNoButton value={false} selected={helpful} onSelect={setHelpful} label="No" />
              </div>
            </div>

            <div>
              <p className="text-sm text-[#2D3748] mb-2">Were your concerns addressed?</p>
              <div className="flex gap-3">
                <YesNoButton value={true} selected={concernsAddressed} onSelect={setConcernsAddressed} label="Yes" />
                <YesNoButton value={false} selected={concernsAddressed} onSelect={setConcernsAddressed} label="No" />
              </div>
            </div>

            <div>
              <p className="text-sm text-[#2D3748] mb-2">Would you recommend us?</p>
              <div className="flex gap-3">
                <YesNoButton value={true} selected={wouldRecommend} onSelect={setWouldRecommend} label="Yes" />
                <YesNoButton value={false} selected={wouldRecommend} onSelect={setWouldRecommend} label="No" />
              </div>
            </div>
          </div>

          {/* Comment */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={16} className="text-[#718096]" />
              <p className="text-sm text-[#2D3748]">Additional comments (optional)</p>
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your thoughts..."
              className="w-full p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-primary"
              rows={3}
              data-testid="feedback-comment"
            />
          </div>

          {/* Privacy Note */}
          <p className="text-xs text-center text-[#A0AEC0]">
            Your feedback is anonymous and helps us improve our services
          </p>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            data-testid="submit-feedback"
            className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Send size={18} />
                Submit Feedback
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
